/**
 * EditorPanel Component
 *
 * Monaco Editor with multi-tab support and AI-powered features.
 */
import { useCallback, useRef, useEffect, useState } from 'react';
import Editor, { OnMount, OnChange } from '@monaco-editor/react';
import { X, Circle, Sparkles, Save, Terminal as TerminalIcon, PanelLeftClose } from 'lucide-react';
import { useProjectStore } from '@/stores/projectStore';
import { useBuildStore } from '@/stores/buildStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useUIStore } from '@/stores/uiStore';
import { createPluginManager, defaultPlugins } from '@/plugins/core';
import { AISuggestionManager, type AISuggestion } from '@/components/editor';
import { piShellUrl, isLanContext } from '@/services/api';
import type { OpenFile } from '@/types';
import clsx from 'clsx';
import type { editor } from 'monaco-editor';
import type * as Monaco from 'monaco-editor';

// 2026-07-14 — image files (png/jpg/jpeg/gif/webp/svg/…) render as an actual
// image, not as blank/garbled plaintext through Monaco. The bytes are NOT in
// OpenFile.content (binaries), so the <img> points at the same raw-serve route
// the preview uses: /api/preview/<project_id>/<path> (routes/preview.py; since
// §9.1 2026-09-05 it is COOKIE-GATED — the <img> carries the `vibe_preview`
// cookie the AuthProvider minted at boot; FileResponse sets the mime from the
// extension).
const IMAGE_EXTS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'bmp', 'ico',
]);

function fileExt(nameOrPath?: string): string {
  if (!nameOrPath) return '';
  const base = nameOrPath.split('/').pop() ?? nameOrPath;
  const i = base.lastIndexOf('.');
  return i > 0 ? base.slice(i + 1).toLowerCase() : '';
}

function isImageFile(file?: { name?: string; path?: string }): boolean {
  return IMAGE_EXTS.has(fileExt(file?.name ?? file?.path));
}

interface EditorTabsProps {
  showTerminal: boolean;
  showTerminalTab: boolean;
  onSelectTerminal: () => void;
  onSelectFile: (fileId: string) => void;
}

function EditorTabs({
  showTerminal,
  showTerminalTab,
  onSelectTerminal,
  onSelectFile,
}: EditorTabsProps) {
  const { openFiles, activeFileId, closeFile } = useProjectStore();

  // Render the row whenever the Terminal tab OR file tabs would show.
  // Plan E Phase 2: Terminal tab shows on LAN even with 0 open files.
  if (!showTerminalTab && openFiles.length === 0) {
    return null;
  }

  return (
    <div
      className="flex items-center bg-vibe-bg-secondary border-b border-vibe-border overflow-x-auto"
      role="tablist"
    >
      {showTerminalTab && (
        <button
          type="button"
          role="tab"
          aria-selected={showTerminal}
          aria-label="Terminal"
          data-testid="pi-terminal-tab"
          onClick={onSelectTerminal}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 border-r border-vibe-border cursor-pointer min-w-0',
            showTerminal
              ? 'bg-vibe-bg text-vibe-text'
              : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary'
          )}
        >
          <TerminalIcon className="w-3 h-3 flex-shrink-0" />
          <span className="text-sm">Terminal</span>
        </button>
      )}
      {openFiles.map((file) => (
        <div
          key={file.id}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 border-r border-vibe-border cursor-pointer group min-w-0',
            activeFileId === file.id && !showTerminal
              ? 'bg-vibe-bg text-vibe-text'
              : 'text-vibe-text-secondary hover:bg-vibe-bg-tertiary'
          )}
          onClick={() => onSelectFile(file.id)}
        >
          {/* Modified indicator */}
          {file.isModified && (
            <Circle className="w-2 h-2 fill-vibe-accent text-vibe-accent flex-shrink-0" />
          )}

          {/* Filename */}
          <span className="text-sm truncate">{file.name}</span>

          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeFile(file.id);
            }}
            className="p-0.5 rounded hover:bg-vibe-border opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 transition-opacity flex-shrink-0"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-vibe-text-secondary">
      <div className="text-6xl mb-4 opacity-20">{'{ }'}</div>
      <p className="text-lg">No file open</p>
      <p className="text-sm mt-2">
        Select a file from the explorer or use{' '}
        <kbd className="px-1.5 py-0.5 bg-vibe-bg-tertiary rounded text-xs">
          Cmd+P
        </kbd>{' '}
        to search
      </p>
    </div>
  );
}

/**
 * EditorHeader — 2026-07-08 window-controls bar for the editor pane. Rendered
 * in BOTH the empty and file-open states so the editor is always closeable
 * (previously the empty state had zero chrome, so it could not be dismissed).
 * `Hide` collapses the editor→preview split's editor panel, enlarging the
 * preview; re-open via the preview toolbar's "Show editor" or by opening a file.
 */
function EditorHeader({ label, onHide }: { label: string; onHide: () => void }) {
  return (
    <div className="flex items-center justify-between px-3 py-1 bg-vibe-bg-secondary border-b border-vibe-border">
      <span className="text-[11px] uppercase tracking-wide text-vibe-text-tertiary truncate">
        {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          data-testid="editor-hide-button"
          onClick={onHide}
          title="Hide editor (enlarge preview)"
          aria-label="Hide editor"
          className="flex items-center gap-1 px-1.5 py-0.5 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text transition-colors"
        >
          <PanelLeftClose className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// 2026-06-29 — static Monaco options (fontSize is applied per-render from
// uiStore). `formatOnPaste`/`formatOnType` are DISABLED: a real paste runs the
// language formatter over the ENTIRE pasted block, which makes large pastes
// feel "finicky"/laggy (verified live — a 70 KB paste is preserved intact but
// slow with formatOnPaste on) and can silently reflow pasted text. Off = paste
// lands instantly, byte-for-byte. The content round-trip itself is unaffected.
export const EDITOR_STATIC_OPTIONS = {
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  fontLigatures: true,
  minimap: { enabled: true, scale: 0.8 },
  scrollBeyondLastLine: false,
  wordWrap: 'on',
  lineNumbers: 'on',
  renderLineHighlight: 'all',
  cursorBlinking: 'smooth',
  cursorSmoothCaretAnimation: 'on',
  smoothScrolling: true,
  padding: { top: 16 },
  automaticLayout: true,
  tabSize: 2,
  insertSpaces: true,
  formatOnPaste: false,
  formatOnType: false,
  bracketPairColorization: { enabled: true },
  guides: {
    bracketPairs: true,
    indentation: true,
  },
} as const;

/**
 * ImageView — 2026-07-14. Renders an image file (png/jpg/svg/…) in the editor
 * pane instead of routing its bytes through Monaco (which shows a blank/garbled
 * text buffer). `<img src>` uses the public /api/preview raw-serve route so it
 * works whether or not the binary content ever loaded into OpenFile.content.
 *
 * The footer's "Describe (AI)" button asks the Pi agent to look at the image
 * with vision (via the describe_selected_image tool). It dispatches the same
 * `vibe:send-to-pi-chat` event the voice bridge uses, so the request lands in
 * the active Pi tab; the tool reads THIS same active-file selection from the
 * project store, so both surfaces (button + voice) describe the same image.
 */
function ImageView({ projectId, file }: { projectId: string; file: OpenFile }) {
  const rel = file.path.replace(/^\/+/, '');
  const src = `/api/preview/${projectId}/${rel.split('/').map(encodeURIComponent).join('/')}`;
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [errored, setErrored] = useState(false);

  const handleDescribe = () => {
    window.dispatchEvent(
      new CustomEvent('vibe:send-to-pi-chat', {
        detail: {
          text:
            `Describe the image I currently have open in the editor (${file.name}). ` +
            `Use the describe_selected_image tool to see it, then tell me what it shows.`,
        },
      }),
    );
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-vibe-bg">
        {errored ? (
          <div className="text-center text-vibe-text-secondary text-sm">
            <p>Could not load image.</p>
            <p className="mt-1 font-mono text-xs break-all">{rel}</p>
          </div>
        ) : (
          <img
            data-testid="editor-image-view"
            src={src}
            alt={file.name}
            onLoad={(e) =>
              setDims({
                w: e.currentTarget.naturalWidth,
                h: e.currentTarget.naturalHeight,
              })
            }
            onError={() => setErrored(true)}
            className="max-w-full max-h-full object-contain rounded shadow-lg"
          />
        )}
      </div>
      <div className="flex items-center justify-between px-3 py-1.5 bg-vibe-bg-secondary border-t border-vibe-border text-xs text-vibe-text-secondary">
        <span className="truncate">
          {file.name}
          {dims ? ` — ${dims.w} × ${dims.h}` : ''}
        </span>
        <button
          type="button"
          data-testid="editor-describe-image-button"
          onClick={handleDescribe}
          title="Ask Pi to describe this image (vision)"
          className="flex items-center gap-1 px-2 py-0.5 rounded bg-vibe-accent/20 text-vibe-accent hover:bg-vibe-accent/30 transition-colors flex-shrink-0"
        >
          <Sparkles className="w-3 h-3" />
          <span>Describe (AI)</span>
        </button>
      </div>
    </div>
  );
}

export function EditorPanel() {
  const {
    openFiles,
    activeFileId,
    setActiveFile,
    updateFileContent,
    updateCursorPosition,
    getActiveFile,
    saveFile,
  } = useProjectStore();
  const tenantId = useTenantStore((s) => s.tenantId);
  // 1.91.0: editor font size is independent from interface font size.
  // Persisted per-browser via uiStore (NOT per-tenant, mirroring viewMode).
  // Monaco does not inherit document.documentElement font-size — its
  // canvas-rendered content has its own fontSize option, so this knob is
  // genuinely orthogonal to the SPA chrome scale.
  const editorFontSize = useUIStore((s) => s.editorFontSize);
  const setEditorCollapsed = useUIStore((s) => s.setEditorCollapsed);

  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const pluginManagerRef = useRef<ReturnType<typeof createPluginManager> | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [showTerminal, setShowTerminal] = useState(false);
  const activeFile = getActiveFile();
  const currentProject = useProjectStore((s) => s.currentProject);
  const previewFile = useBuildStore((s) => s.previewFile);

  // Plan E Phase 2: Terminal tab visibility is gated on LAN context AND
  // a known tenantId (so the iframe URL can be constructed).
  const showTerminalTab = isLanContext() && tenantId !== null;

  // 2026-06-27 — the preview follows the file being edited. Whenever the
  // active editor tab changes to a previewable (.html) file, point the
  // preview pane at it (served from source, no build). This mirrors the
  // explorer-click behavior in FilesPanel so that ANY way of opening a
  // file — click, Cmd+P, or an agent writing + opening it — is visible in
  // the preview without a manual dropdown selection.
  const activeFilePath = activeFile?.path;
  useEffect(() => {
    if (!currentProject || !activeFilePath) return;
    if (!activeFilePath.toLowerCase().endsWith('.html')) return;
    previewFile(currentProject.id, activeFilePath.replace(/^\/+/, ''));
  }, [currentProject, activeFilePath, previewFile]);

  // 2026-07-08 — auto-expand the editor when a file is opened while it is
  // collapsed (opening a file into a hidden editor would be invisible). Keyed
  // ONLY on activeFileId change; editorCollapsed is read via getState (NOT a
  // dep) so manually hiding the editor while a file stays open does not
  // immediately re-expand it.
  useEffect(() => {
    if (activeFileId && useUIStore.getState().editorCollapsed) {
      useUIStore.getState().setEditorCollapsed(false);
    }
  }, [activeFileId]);

  // Handle AI suggestion events
  useEffect(() => {
    const handleExplanation = (e: CustomEvent<{ content: string; code: string }>) => {
      const newSuggestion: AISuggestion = {
        id: `explain-${Date.now()}`,
        originalCode: e.detail.code,
        suggestedCode: e.detail.content,
        explanation: 'AI Explanation',
        action: 'document',
        position: { lineNumber: 1, column: 1 },
      };
      setAiSuggestions((prev) => [...prev, newSuggestion]);
    };

    const handleError = (e: CustomEvent<{ error: string; action: string }>) => {
      console.error(`AI ${e.detail.action} error:`, e.detail.error);
    };

    window.addEventListener('vibe:ai:explanation', handleExplanation as EventListener);
    window.addEventListener('vibe:ai:error', handleError as EventListener);

    return () => {
      window.removeEventListener('vibe:ai:explanation', handleExplanation as EventListener);
      window.removeEventListener('vibe:ai:error', handleError as EventListener);
    };
  }, []);

  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // B2 (2026-05-06): minimal TS LSP config — Tier 1.
    // Without this, Monaco's TS service runs with default lib (ESNext only)
    // and produces squigglies on every `import React from 'react'` and JSX
    // element. Tier 2 (extraLibs for react types) and Tier 3 (ATA) are
    // separate workstreams.
    //
    // R2 reviewer Read node_modules/monaco-editor/monaco.d.ts:9332-9362
    // (monaco-editor 0.55.1 bundled with @monaco-editor/react ^4.6.0):
    //   ModuleResolutionKind { Classic = 1, NodeJs = 2 }   (Bundler ABSENT)
    //   JsxEmit.ReactJSX = 4
    //   ScriptTarget.ES2020 = 7
    //   ModuleKind.ESNext = 99
    //
    // try/catch graceful fallback: if monaco TS API throws (malformed
    // enum / version mismatch), log + continue. Editor still mounts with
    // default LSP — Tier 1 is an enhancement, not a core feature.
    try {
      const tsConfig = {
        target: monaco.languages.typescript.ScriptTarget.ES2020,
        module: monaco.languages.typescript.ModuleKind.ESNext,
        moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
        jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
        esModuleInterop: true,
        allowJs: true,
        allowSyntheticDefaultImports: true,
        strict: false,
        skipLibCheck: true,
      };
      monaco.languages.typescript.typescriptDefaults.setCompilerOptions(tsConfig);
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES2020,
        module: monaco.languages.typescript.ModuleKind.ESNext,
        moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
        jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
        esModuleInterop: true,
        allowJs: true,
        allowSyntheticDefaultImports: true,
      });
    } catch (err) {
      // Per R2 Concern 3 — graceful fallback, do NOT swallow silently.
      console.error('[B2] Failed to configure Monaco TS LSP:', err);
    }

    // Initialize plugin manager
    pluginManagerRef.current = createPluginManager();

    // Register all default plugins
    defaultPlugins.forEach((plugin) => {
      pluginManagerRef.current?.register(plugin);
    });

    // Activate all plugins
    pluginManagerRef.current.activateAll(monaco, editor);

    // Set up cursor position tracking
    editor.onDidChangeCursorPosition((e) => {
      if (activeFileId) {
        updateCursorPosition(
          activeFileId,
          e.position.lineNumber,
          e.position.column
        );
      }
    });
  }, [activeFileId, updateCursorPosition]);

  // Cleanup plugins on unmount
  useEffect(() => {
    return () => {
      pluginManagerRef.current?.deactivateAll();
    };
  }, []);

  const handleEditorChange: OnChange = useCallback(
    (value) => {
      if (activeFileId && value !== undefined) {
        updateFileContent(activeFileId, value);
      }
    },
    [activeFileId, updateFileContent]
  );

  // No files open AND no Terminal tab to show — fall back to EmptyState only.
  if (openFiles.length === 0 && !showTerminalTab) {
    return (
      <div className="flex flex-col h-full w-full overflow-hidden">
        <EditorHeader label="Editor" onHide={() => setEditorCollapsed(true)} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <EditorHeader
        label={activeFile?.path ?? 'Editor'}
        onHide={() => setEditorCollapsed(true)}
      />
      {/* Tabs */}
      <EditorTabs
        showTerminal={showTerminal}
        showTerminalTab={showTerminalTab}
        onSelectTerminal={() => setShowTerminal(true)}
        onSelectFile={(fileId) => {
          setShowTerminal(false);
          setActiveFile(fileId);
        }}
      />

      {/* Plan E Phase 2: Terminal pane — iframe to per-tenant Pi shell.
          LAN-only v1; sandbox allow-same-origin needed for Authentik
          cookie inheritance from .t440.gamemagick.internal. */}
      {showTerminal && tenantId && (
        <div className="flex-1">
          <iframe
            data-testid="pi-terminal-iframe"
            src={piShellUrl(tenantId)}
            title={`Pi shell — ${tenantId}`}
            sandbox="allow-scripts allow-same-origin allow-forms"
            className="w-full h-full border-0"
          />
        </div>
      )}

      {/* B3 (2026-05-06): info badge when active file is index.html.
          User reported confusion editing index.html — `<body>` is owned
          by React (`<div id="root">` per backend/routes/projects.py:401-415)
          so body content edits are invisible. `<head>` edits DO propagate
          via Vite rebuild on next save. Tier (a) affordance only;
          tier (b) sidecar + tier (c) HMR explicitly out of scope per
          backlog §3. */}
      {!showTerminal && activeFile?.path?.endsWith('index.html') && (
        <div
          data-testid="index-html-info-badge"
          className="flex items-center px-3 py-2 bg-vibe-info/10 border-b border-vibe-info/30 text-xs text-vibe-text-secondary"
        >
          <span>
            Changes to <code className="font-mono text-vibe-info">&lt;head&gt;</code>{' '}
            (title, meta, scripts) appear on next rebuild.{' '}
            <code className="font-mono text-vibe-info">&lt;body&gt;</code> is owned by
            React — edit <code className="font-mono text-vibe-info">App.tsx</code> instead.
          </span>
        </div>
      )}

      {/* Editor — hidden when Terminal tab is active (iframe takes flex-1 above) */}
      {!showTerminal && (
        <div className="flex-1">
          {activeFile ? (
            isImageFile(activeFile) && currentProject ? (
              // 2026-07-14 — image files render as an image, not Monaco text.
              <ImageView projectId={currentProject.id} file={activeFile} />
            ) : (
              <Editor
                key={activeFile.id}
                height="100%"
                language={activeFile.language}
                value={activeFile.content}
                theme="vs-dark"
                onChange={handleEditorChange}
                onMount={handleEditorMount}
                options={{
                  ...EDITOR_STATIC_OPTIONS,
                  fontSize: editorFontSize,
                  // 2026-08-17 empty-editor fix: a buffer whose content never
                  // loaded ('unfetched' in-flight / 'error' after a failed
                  // read) is read-only — typing into it would look like
                  // editing the real file.
                  readOnly:
                    activeFile.contentSource === 'unfetched' ||
                    activeFile.contentSource === 'error',
                }}
              />
            )
          ) : (
            <EmptyState />
          )}
        </div>
      )}

      {/* Status Bar — hidden when Terminal tab is active OR the active file is an
          image (ImageView renders its own footer with the Describe action). */}
      {!showTerminal && activeFile && !isImageFile(activeFile) && (
        <div className="flex items-center justify-between px-3 py-1 bg-vibe-bg-secondary border-t border-vibe-border text-xs text-vibe-text-secondary">
          <div className="flex items-center gap-4">
            <span>{activeFile.language}</span>
            {activeFile.cursorPosition && (
              <span>
                Ln {activeFile.cursorPosition.line}, Col{' '}
                {activeFile.cursorPosition.column}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="editor-save-button"
              onClick={() => {
                if (activeFileId) void saveFile(activeFileId);
              }}
              // 2026-08-17 empty-editor fix: never saveable while the buffer's
              // real content is unknown ('unfetched'/'error') — the visible
              // half of the projectStore.saveFile guard.
              disabled={
                !activeFile.isModified ||
                activeFile.contentSource === 'unfetched' ||
                activeFile.contentSource === 'error'
              }
              className={clsx(
                'flex items-center gap-1 px-2 py-0.5 rounded transition-colors',
                activeFile.isModified &&
                  activeFile.contentSource !== 'unfetched' &&
                  activeFile.contentSource !== 'error'
                  ? 'bg-vibe-accent/20 text-vibe-accent hover:bg-vibe-accent/30'
                  : 'text-vibe-text-tertiary opacity-50 cursor-not-allowed'
              )}
              title={
                activeFile.contentSource === 'unfetched' ||
                activeFile.contentSource === 'error'
                  ? 'File content not loaded — saving is disabled'
                  : activeFile.isModified
                    ? 'Save file (Cmd/Ctrl+S)'
                    : 'No unsaved changes'
              }
            >
              <Save className="w-3 h-3" />
              <span>Save</span>
            </button>
            <button
              onClick={() => setAiEnabled(!aiEnabled)}
              className={clsx(
                'flex items-center gap-1 px-2 py-0.5 rounded transition-colors',
                aiEnabled
                  ? 'bg-vibe-accent/20 text-vibe-accent'
                  : 'text-vibe-text-tertiary hover:text-vibe-text'
              )}
              title={aiEnabled ? 'AI features enabled' : 'AI features disabled'}
            >
              <Sparkles className="w-3 h-3" />
              <span>AI</span>
            </button>
            <span>UTF-8</span>
            <span>Spaces: 2</span>
          </div>
        </div>
      )}

      {/* AI Suggestions */}
      <AISuggestionManager
        suggestions={aiSuggestions}
        onAccept={(suggestion) => {
          // Apply the suggestion to the editor
          if (editorRef.current) {
            const selection = editorRef.current.getSelection();
            if (selection) {
              editorRef.current.executeEdits('ai-suggestion', [
                {
                  range: selection,
                  text: suggestion.suggestedCode,
                  forceMoveMarkers: true,
                },
              ]);
            }
          }
          setAiSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        }}
        onReject={(suggestion) => {
          setAiSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        }}
        onModify={(suggestion, modifiedCode) => {
          if (editorRef.current) {
            const selection = editorRef.current.getSelection();
            if (selection) {
              editorRef.current.executeEdits('ai-suggestion', [
                {
                  range: selection,
                  text: modifiedCode,
                  forceMoveMarkers: true,
                },
              ]);
            }
          }
          setAiSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        }}
      />
    </div>
  );
}
